import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FiLock, FiArrowRight, FiAlertCircle, FiCheckCircle } from 'react-icons/fi';
import Logo from '../components/Logo';
import { useSite } from '../context/SiteContext';

export default function ResetPassword() {
  const { token } = useParams();
  const navigate = useNavigate();
  const site = useSite();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(true);
  const [valid, setValid] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    fetch(`/api/auth/reset-password/${token}`)
      .then(r => r.json())
      .then(data => {
        setValid(data.valid);
        if (!data.valid) setError(data.error || 'Invalid or expired reset link.');
      })
      .catch(() => setError('Unable to verify reset link.'))
      .finally(() => setValidating(false));
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 6) { setError('Password must be at least 6 characters'); return; }
    if (password !== confirm) { setError('Passwords do not match'); return; }
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`/api/auth/reset-password/${token}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password })
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error || 'Failed to reset password'); setLoading(false); return; }
      setSuccess(true);
      setTimeout(() => navigate('/login'), 3000);
    } catch {
      setError('Failed to reset password');
    }
    setLoading(false);
  };

  const inputStyle = {
    width: '100%', padding: '14px 14px 14px 42px', borderRadius: 10,
    border: '1px solid var(--border)', background: 'var(--bg-input)',
    color: 'var(--text-primary)', fontSize: 14, outline: 'none', transition: 'border-color 0.2s',
    boxSizing: 'border-box'
  };

  return (
    <div className="gradient-bg" style={{
      minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16
    }}>
      <motion.div
        initial={{ opacity: 0, scale: 0.9, y: 30 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ duration: 0.5, type: 'spring' }}
        style={{
          width: '100%', maxWidth: 420, background: 'var(--bg-card)',
          borderRadius: 20, padding: 'clamp(24px, 5vw, 48px) clamp(20px, 4vw, 36px)',
          border: '1px solid var(--border)', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.3)',
          position: 'relative', zIndex: 1
        }}
      >
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <div style={{ margin: '0 auto 16px', width: 64, height: 64 }}>
            {site?.logoUrl ? (
              <img src={site.logoUrl} alt="" style={{ width: 64, height: 64, borderRadius: 14, objectFit: 'contain' }} />
            ) : (
              <Logo size={64} />
            )}
          </div>
          <h1 style={{ fontSize: 24, fontWeight: 700, marginBottom: 4, color: 'var(--text-primary)' }}>Reset Password</h1>
          <p style={{ color: 'var(--text-muted)', fontSize: 14 }}>Enter your new password below</p>
        </div>

        {validating ? (
          <div style={{ textAlign: 'center', padding: '20px 0', color: 'var(--text-muted)', fontSize: 14 }}>
            Verifying reset link...
          </div>
        ) : success ? (
          <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}
            style={{ textAlign: 'center', padding: '20px 0' }}
          >
            <div style={{ width: 56, height: 56, borderRadius: '50%', background: 'rgba(22,163,74,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
              <FiCheckCircle size={28} color="#16a34a" />
            </div>
            <h2 style={{ margin: '0 0 8px', fontSize: 20, fontWeight: 700, color: 'var(--text-primary)' }}>Password Reset</h2>
            <p style={{ color: 'var(--text-muted)', fontSize: 14, margin: 0 }}>Redirecting to login...</p>
          </motion.div>
        ) : !valid ? (
          <div style={{ textAlign: 'center', padding: '20px 0' }}>
            <div style={{ width: 56, height: 56, borderRadius: '50%', background: 'rgba(220,38,38,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
              <FiAlertCircle size={28} color="#dc2626" />
            </div>
            <p style={{ color: '#dc2626', fontSize: 14, fontWeight: 600, margin: '0 0 16px' }}>{error}</p>
            <button onClick={() => navigate('/login')} style={{
              padding: '10px 24px', borderRadius: 10, border: '1px solid var(--border)',
              background: 'var(--bg-card)', color: 'var(--text-primary)', cursor: 'pointer', fontSize: 14, fontWeight: 500
            }}>Back to Login</button>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            {error && (
              <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}
                style={{
                  padding: '12px 16px', borderRadius: 10, marginBottom: 20,
                  background: 'rgba(220,38,38,0.06)', border: '1px solid rgba(220,38,38,0.2)',
                  color: '#dc2626', fontSize: 13, display: 'flex', alignItems: 'center', gap: 8
                }}
              >
                <FiAlertCircle size={16} /> {error}
              </motion.div>
            )}

            <div style={{ marginBottom: 16 }}>
              <label style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-secondary)', marginBottom: 6, display: 'block' }}>
                New Password
              </label>
              <div style={{ position: 'relative' }}>
                <FiLock size={16} style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
                <input type="password" value={password} onChange={e => setPassword(e.target.value)} required
                  placeholder="Min 6 characters" style={inputStyle}
                  onFocus={e => e.target.style.borderColor = '#2563eb'}
                  onBlur={e => e.target.style.borderColor = 'var(--border)'}
                />
              </div>
            </div>

            <div style={{ marginBottom: 24 }}>
              <label style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-secondary)', marginBottom: 6, display: 'block' }}>
                Confirm Password
              </label>
              <div style={{ position: 'relative' }}>
                <FiLock size={16} style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
                <input type="password" value={confirm} onChange={e => setConfirm(e.target.value)} required
                  placeholder="Re-enter new password" style={inputStyle}
                  onFocus={e => e.target.style.borderColor = '#2563eb'}
                  onBlur={e => e.target.style.borderColor = 'var(--border)'}
                />
              </div>
            </div>

            <motion.button type="submit" disabled={loading}
              whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}
              style={{
                width: '100%', padding: '14px', borderRadius: 10, border: 'none',
                background: '#2563eb', color: '#fff', fontSize: 15, fontWeight: 600, cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                boxShadow: '0 4px 12px rgba(37,99,235,0.3)', opacity: loading ? 0.7 : 1
              }}
            >
              {loading ? 'Resetting...' : <>Reset Password <FiArrowRight size={16} /></>}
            </motion.button>
          </form>
        )}
      </motion.div>
    </div>
  );
}
